/**
 * ButtonLink
 *
 * Combines the Codex Button with React Router's Link so you get
 * full navigation behaviour (client-side routing, prefetch, etc.)
 * without swapping to a plain `<a>`.
 *
 * @example
 * <ButtonLink to="/register" variant="filled" size="sm">
 *   Get started
 * </ButtonLink>
 *
 * @example
 * <ButtonLink to="/settings" variant="outlined" size="sm">
 *   Settings
 * </ButtonLink>
 */

import React from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Button } from './Button';
import type { ButtonProps } from './types';

export interface ButtonLinkProps extends ButtonProps {
  /** React Router destination — required. */
  to: LinkProps['to'];
  /** Optional React Router state passed to the history entry. */
  state?: LinkProps['state'];
  /** Replace the current history entry instead of pushing. */
  replace?: LinkProps['replace'];
}

/**
 * A Codex Button that navigates via React Router.
 *
 * All Button props are supported. `href` and `onClick`-for-navigation
 * are intentionally omitted — use `to` instead.
 */
export const ButtonLink = React.forwardRef<HTMLButtonElement, ButtonLinkProps>(
  ({ to, state, replace, children, ...buttonProps }, ref) => {
    return (
      <Button ref={ref} component={Link} to={to} state={state} replace={replace} {...buttonProps}>
        {children}
      </Button>
    );
  },
);

ButtonLink.displayName = 'ButtonLink';

export default ButtonLink;
